import { Text, View, StyleSheet, Image } from '@react-pdf/renderer'
import { hydraScoreRating, normalizeKeys } from '../_data/helper'
import { PDFReportFeatureProps } from '..'
import HydraScoreCircle from './score'
import { pdfFontSize, pdfPadding } from '../_data/style-define'

const styles = StyleSheet.create({
  executiveSummarySection: {
    marginTop: pdfPadding.large,
    pageBreakBefore: '',
  },
  executiveSummaryTitle: {
    color: '#00B5A3',
    fontSize: pdfFontSize.title,
    fontWeight: 'bold',
    marginBottom: pdfPadding.small,
  },
  title: {
    color: '#00B5A3',
    fontSize: pdfFontSize.title,
    marginBottom: pdfPadding.medium,
  },
  gsaList: {
    fontSize: pdfFontSize.text,
    lineHeight: 1.6,
    marginBottom: pdfPadding.large,
  },
  scoreSection: {
    marginBottom: pdfPadding.large,
  },
  scoreRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: pdfPadding.medium,
    alignItems: 'center',
  },
  scoreColumn: {
    alignItems: 'center',
  },
  scoreTitle: {
    fontSize: pdfFontSize.subtitle,
    fontWeight: 'bold',
    marginBottom: pdfPadding.medium,
  },
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: 40,
    marginTop: pdfPadding.medium,
  },
  infoSection: {
    width: '45%',
    marginBottom: pdfPadding.large,
  },
  infoTitle: {
    fontSize: pdfFontSize.subtitle,
    fontWeight: 'bold',
    marginBottom: pdfPadding.small,
  },
  infoContent: {
    fontSize: pdfFontSize.text,
    lineHeight: 1.6,
  },
  bulletPoint: {
    marginBottom: pdfPadding.small,
  },
  topScoresRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  scoreImage: {
    width: 72,
    height: 72,
  },
  scoreImageSmall: {
    width: 48,
    height: 48,
  },
  // Bottom scores section
  bottomScoresSection: {
    marginTop: pdfPadding.medium,
    paddingTop: pdfPadding.medium,
  },
  bottomScoresRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: pdfPadding.small,
  },
  bottomScoreColumn: {
    width: '18%',
    display: 'flex',
    alignItems: 'center',
  },
  bottomScoreTitle: {
    fontSize: pdfFontSize.subtitle,
    fontWeight: 'bold',
    marginBottom: pdfPadding.medium,
  },
  ratingsColumn: {
    flex: 2,
    minWidth: 180,
    paddingLeft: pdfPadding.medium,
  },
  ratingsTitle: {
    fontSize: pdfFontSize.subtitle,
    fontWeight: 'bold',
  },
  ratingRow: {},
  ratingText: {
    fontSize: pdfFontSize.text,
    marginTop: pdfPadding.small,
  },
  ratingTextScore: {
    fontSize: pdfFontSize.text,
    marginTop: pdfPadding.medium,
  },
  divider: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginVertical: pdfPadding.small,
  },
  footnote: {
    fontSize: pdfFontSize.text,
    marginTop: pdfPadding.small,
    fontStyle: 'italic',
  },
  columnHeaders: {
    flexDirection: 'row',
    borderBottomColor: '#E5E7EB',
    marginTop: pdfPadding.large,
  },
})

const ExecutiveSummaryScore = ({
  data,
  ability,
  type,
}: PDFReportFeatureProps) => {
  if (type === 'parcel') {
    return (
      <View>
        <View style={styles.topScoresRow} wrap={false}>
          {ability.hydraScore && (
            <View style={styles.scoreColumn}>
              <Text style={styles.scoreTitle}>Hydra Score</Text>
              <HydraScoreCircle
                size={styles.scoreImage.width}
                score={data.property.data['hydraScore']}
              />
            </View>
          )}
          {ability.soilScore && (
            <View style={styles.scoreColumn}>
              <Text style={styles.scoreTitle}>Soil Score</Text>
              <HydraScoreCircle
                text="Soil"
                size={styles.scoreImage.width}
                score={data.property.data['ss']}
              />
            </View>
          )}
          <View style={styles.scoreColumn}>
            <Text style={styles.scoreTitle}>Rating</Text>
            <Text style={styles.ratingTextScore}>
              {hydraScoreRating(data.property.data.hydraScore)}
            </Text>
          </View>
          <View style={styles.scoreColumn}>
            <Text style={styles.scoreTitle}>Acres</Text>
            <Text style={styles.ratingTextScore}>
              {parseFloat(data.property.data.acre)
                .toFixed(2)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}{' '}
              ac
            </Text>
          </View>
        </View>

        <View style={styles.bottomScoresSection} wrap={false}>
          <View style={styles.bottomScoresRow}>
            {ability.hydraScore && (
              <View style={styles.bottomScoreColumn}>
                <Text style={styles.bottomScoreTitle}>Hydra Score</Text>

                <HydraScoreCircle
                  size={styles.scoreImageSmall.width}
                  score={data.property.data['hydraScore']}
                />
              </View>
            )}
            {ability.soilScore && (
              <View style={styles.bottomScoreColumn}>
                <Text style={styles.bottomScoreTitle}>Soil Score</Text>
                <HydraScoreCircle
                  text="Soil"
                  size={styles.scoreImageSmall.width}
                  score={data.property.data['ss']}
                />
              </View>
            )}

            <View style={styles.ratingsColumn}>
              <Text style={styles.ratingsTitle}>Ratings</Text>

              {ability.gsaRatings && (
                <View style={styles.ratingRow}>
                  {[
                    ...new Map(
                      data.property.feature.properties.gsa_info.map(
                        (gsa: any) => [gsa.gsa_name, gsa]
                      )
                    ).values(),
                  ].map((gsa: any, gsaIndex: number) => (
                    <Text key={gsaIndex} style={styles.ratingText}>
                      {`GSA: ${gsa.gsa_name} (${gsa.gsa_rating === -1 ? 'NR' : gsa.gsa_rating})`}
                    </Text>
                  ))}
                </View>
              )}
              {ability.wdRatings && (
                <View style={styles.ratingRow}>
                  {data.property.feature.properties.wd_info.map(
                    (wd: any, wdIndex: number) => (
                      <Text key={wdIndex} style={styles.ratingText}>
                        {`WD: ${wd.wd_name} (${wd.wd_rating === -1 ? 'NR' : wd.wd_rating})`}
                      </Text>
                    )
                  )}
                </View>
              )}
            </View>
          </View>
        </View>

        <View style={styles.infoGrid}>
          <InfoSection
            ability={ability.hydrologicRegion}
            title="Hydrologic Region"
            values={[
              data.property.feature.properties.hydrologic_info
                .hydrologic_region,
            ]}
          />

          <InfoSection
            ability={true}
            title="Watershed"
            values={[
              data.property.feature.properties.hydrologic_info.watershed,
            ]}
          />

          <InfoSection
            ability={true}
            title="Groundwater Basin"
            values={[
              data.property.feature.properties.hydrologic_info.basin_name,
            ]}
          />

          <InfoSection
            ability={true}
            title="Groundwater Subbasin"
            values={[
              data.property.feature.properties.hydrologic_info.subbasin_name,
            ]}
          />

          <InfoSection
            ability={ability.gsaAndWaterDistrictBoundaries}
            title="Groundwater Sustainability Agency"
            values={data.property.feature.properties.gsa_info.map(
              (gsa: any) => gsa.gsa_name
            )}
          />

          <InfoSection
            ability={ability.gsaAndWaterDistrictBoundaries}
            title="Water District(s)"
            values={data.property.feature.properties.wd_info.map(
              (wd: any) => wd.wd_name
            )}
          />

          <View style={{ ...styles.infoSection, paddingTop: pdfPadding.small }}>
            <Image
              src={data['aerial-view']}
              style={{
                height: 180,
                width: '100%',
              }}
            />
          </View>
          <View style={{ ...styles.infoSection, paddingTop: pdfPadding.large }}>
            <Text style={styles.infoTitle}>
              {data.property.feature.properties.owner}
            </Text>

            <View
              style={{ ...styles.infoContent, paddingLeft: pdfPadding.small }}
            >
              <Text>
                •{'  '} Acres:{' '}
                {parseFloat(data.property.data.acre)
                  .toFixed(2)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}{' '}
                ac
              </Text>
              <Text>
                •{'  '} County: {data.property.data.county}
              </Text>

              {ability.assemblyAndSenateDistricts &&
                Object.entries(data.stateDistricts).map(([key, value]) => (
                  <Text key={key}>
                    •{'  '} {key} : {String(value)}
                  </Text>
                ))}
            </View>
          </View>
        </View>
      </View>
    )
  }

  const groundwaterBasin: string[] = []
  const groundwaterSubbasin: string[] = []

  data.parcels.forEach((parcel: any) => {
    // Iterate through each subbasin in the parcel
    if (
      !groundwaterBasin.includes(
        parcel.feature.properties.hydrologic_info.basin_name
      )
    )
      groundwaterBasin.push(
        parcel.feature.properties.hydrologic_info.basin_name
      )
    if (
      !groundwaterSubbasin.includes(
        parcel.feature.properties.hydrologic_info.subbasin_name
      )
    )
      groundwaterSubbasin.push(
        parcel.feature.properties.hydrologic_info.subbasin_name
      )
  })

  const subbasinExecutiveSummaries: { [key: string]: any } = {}
  const waterDistrictExecutiveSummaries: { [key: string]: any } = {}
  const gsatExecutiveSummaries: { [key: string]: any } = {}

  data.parcels.forEach((parcel: any) => {
    // Iterate through each subbasin in the parcel
    if (
      !Object.keys(subbasinExecutiveSummaries).includes(
        parcel.feature.properties.hydrologic_info.subbasin_name
      )
    )
      subbasinExecutiveSummaries[
        parcel.feature.properties.hydrologic_info.subbasin_name
      ] = parcel.feature.properties.hydrologic_info.executive_summary
    if (
      !Object.keys(waterDistrictExecutiveSummaries).includes(
        parcel.feature.properties.wd_info.wd_name
      )
    )
      waterDistrictExecutiveSummaries[
        parcel.feature.properties.wd_info.wd_name
      ] = parcel.feature.properties.wd_info.wd_executive_summary
    if (
      !Object.keys(gsatExecutiveSummaries).includes(
        parcel.feature.properties.gsa_info.gsa_name
      )
    )
      gsatExecutiveSummaries[parcel.feature.properties.gsa_info.gsa_name] =
        parcel.feature.properties.gsa_info.gsa_allocation_status_update
  })

  const showRatings = ability.gsaRatings || ability.wdRatings

  return (
    <View>
      <View style={styles.topScoresRow} wrap={false}>
        {ability.hydraScore && (
          <View style={styles.scoreColumn}>
            <Text style={styles.scoreTitle}>Ranch Hydra Score</Text>
            <HydraScoreCircle
              size={styles.scoreImage.width}
              score={
                data.summary['Geometrically Weighted Aggregate HydraSCORE']
              }
            />
          </View>
        )}
        {ability.soilScore && (
          <View style={styles.scoreColumn}>
            <Text style={styles.scoreTitle}>Ranch Soil Score</Text>
            <HydraScoreCircle
              size={styles.scoreImage.width}
              text="Soil"
              score={data.summary['Geometrically Weighted Aggregate SS']}
            />
          </View>
        )}
        <View style={styles.scoreColumn}>
          <Text style={styles.scoreTitle}>Rating</Text>
          <Text style={styles.ratingTextScore}>
            {hydraScoreRating(
              data.summary['Geometrically Weighted Aggregate HydraSCORE']
            ).toUpperCase()}
          </Text>
        </View>
        <View style={styles.scoreColumn}>
          <Text style={styles.scoreTitle}>Total Acres</Text>
          <Text style={styles.ratingTextScore}>
            {data.summary['Total Acres']
              .toFixed(2)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}{' '}
            ac
          </Text>
        </View>
      </View>

      {/* Bottom Scores Section */}
      <View>
        <View style={styles.columnHeaders}>
          {ability.apnDisplay && (
            <View
              style={{ ...styles.bottomScoreColumn, alignItems: 'flex-start' }}
            >
              <Text style={styles.bottomScoreTitle}>APN</Text>
            </View>
          )}
          <View style={styles.bottomScoreColumn}>
            <Text style={styles.bottomScoreTitle}>Acres</Text>
          </View>
          {ability.hydraScore && (
            <View style={styles.bottomScoreColumn}>
              <Text style={styles.bottomScoreTitle}>Hydra Score</Text>
            </View>
          )}
          {ability.soilScore && (
            <View style={styles.bottomScoreColumn}>
              <Text style={styles.bottomScoreTitle}>Soil Score</Text>
            </View>
          )}
          {showRatings && (
            <View style={styles.ratingsColumn}>
              <Text style={styles.ratingsTitle}>Ratings</Text>
            </View>
          )}
        </View>

        {data.parcels.map((parcel: any, index: any) => {
          return (
            <View key={index} wrap={false}>
              <View
                style={{
                  ...styles.bottomScoresRow,
                  alignItems: 'center',
                }}
              >
                {ability.apnDisplay && (
                  <View
                    style={{
                      ...styles.bottomScoreColumn,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Text style={styles.ratingText}>{parcel.data.apn}</Text>
                  </View>
                )}
                <View style={styles.bottomScoreColumn}>
                  <Text style={styles.ratingText}>{parcel.data.acre} ac</Text>
                </View>
                {ability.hydraScore && (
                  <View style={styles.bottomScoreColumn}>
                    <HydraScoreCircle
                      size={styles.scoreImageSmall.width}
                      score={parcel.data['hydraScore']}
                    />
                  </View>
                )}
                {ability.soilScore && (
                  <View style={styles.bottomScoreColumn}>
                    <HydraScoreCircle
                      text="Soil"
                      size={styles.scoreImageSmall.width}
                      score={parcel.data['ss']}
                    />
                  </View>
                )}
                {showRatings && (
                  <View
                    style={{
                      ...styles.ratingsColumn,
                      alignItems: 'flex-start',
                    }}
                  >
                    {ability.gsaRatings && (
                      <View style={styles.ratingRow}>
                        {[
                          ...new Map(
                            parcel.feature.properties.gsa_info.map(
                              (gsa: any) => [gsa.gsa_name, gsa]
                            )
                          ).values(),
                        ].map((gsa: any, gsaIndex: number) => (
                          <Text key={gsaIndex} style={styles.ratingText}>
                            {`GSA: ${gsa.gsa_name} (${gsa.gsa_rating === -1 ? 'NR' : gsa.gsa_rating})`}
                          </Text>
                        ))}
                      </View>
                    )}
                    {ability.wdRatings && (
                      <View style={styles.ratingRow}>
                        {parcel.feature.properties.wd_info.map(
                          (wd: any, wdIndex: number) => (
                            <Text key={wdIndex} style={styles.ratingText}>
                              {`WD: ${wd.wd_name} (${wd.wd_rating === -1 ? 'NR' : wd.wd_rating})`}
                            </Text>
                          )
                        )}
                      </View>
                    )}
                  </View>
                )}
              </View>
              {index !== data.parcels.length - 1 && (
                <View style={styles.divider} />
              )}
            </View>
          )
        })}
      </View>
      {/* <View style={styles.executiveSummarySection}>
        <Text style={styles.executiveSummaryTitle}>Executive Summaries</Text>
        <InfoSection
          ability={ability.subbasinExecutiveSumary}
          title="Subbasin Executive Summaries"
          values={data.summary['Hydrologic Region(s)']}
        />
        <InfoSection
          ability={ability.subbasinExecutiveSumary}
          title="Hydrologic Region"
          values={data.summary['Hydrologic Region(s)']}
        />
        <InfoSection
          ability={ability.subbasinExecutiveSumary}
          title="Hydrologic Region"
          values={data.summary['Hydrologic Region(s)']}
        />
      </View> */}

      <View style={styles.infoGrid}>
        <InfoSection
          ability={ability.hydrologicRegion}
          title="Hydrologic Region"
          values={data.summary['Hydrologic Region(s)']}
        />

        <InfoSection
          ability={true}
          title="Watershed"
          values={data.summary['Watershed(s)']}
        />

        <InfoSection
          ability={true}
          title="Groundwater Basin"
          values={groundwaterBasin}
        />

        <InfoSection
          ability={true}
          title="Groundwater Subbasin"
          values={groundwaterSubbasin}
        />

        <InfoSection
          ability={ability.gsaAndWaterDistrictBoundaries}
          title="Groundwater Sustainability Agency"
          values={data.summary['GSA(s)']}
        />

        <InfoSection
          ability={ability.gsaAndWaterDistrictBoundaries}
          title="Water District(s)"
          values={data.summary['Water District(s)']}
        />
      </View>
    </View>
  )
}

export default ExecutiveSummaryScore

const InfoSection = ({
  ability = true,
  title,
  values = [],
}: {
  ability?: boolean
  title: string
  values: any[] | undefined
}) => {
  if (!ability) return null

  return (
    <View style={styles.infoSection} wrap={false}>
      <Text style={styles.infoTitle}>{title}</Text>
      {values.map((value, index) => (
        <Text key={`${index}-${value}`} style={styles.infoContent}>
          {value}
        </Text>
      ))}
    </View>
  )
}
